import { Dispatch, Reducer, useMemo, useReducer, useState } from 'react';

export const useEntityState = <T>(
  initState?: Partial<T>
): [
  Partial<T>,
  (property: keyof T) => (value: unknown) => void,
  (value: Partial<T>) => void,
  boolean
] => {
  const [originalState] = useState<Partial<T>>(JSON.parse(JSON.stringify(initState || {})));
  const [state, setState] = useReducer<Reducer<Partial<T>, Partial<T> | { property: string, value: any } | null>>(
    (state: Partial<T>, action) => {
      if (!action) {
        return {};
      }
      if (!(action as { property: string, value: any }).property) {
        return action || {};
      }
      const { property, value } = action as { property: string, value: any };
      return setFieldValue<Partial<T>>({...state}, property, value);
    },
    initState || {},
  );

  const onChange = (property: keyof T) => (value: unknown) =>
    setState({ property, value } as { property: string, value: any });

  const isChanged = useMemo(() => {
    return Object.keys(state).some((key) => {
      if (typeof state[key] === 'object') {
        return !deepEqual(state[key], originalState[key]);
      }
      return state[key] !== originalState[key];
    });
  }, [state, originalState]);

  return [state, onChange, setState as Dispatch<Partial<T>>, isChanged];
};

export function setFieldValue<T>(instance: T, fieldName: string | number | symbol, value: any) {
  if(typeof fieldName === 'string' && fieldName.includes('.')) {
    const topField = (fieldName).split('.')[0];
    instance[topField] = setFieldValue(instance[topField], fieldName.replace(`${topField}.`, ''), value);
    return instance;
  }
  instance[fieldName] = value;
  return instance;
}

function deepEqual(object1, object2) {
  if (object1 === null && object2 === null) {
    return true;
  }
  if (object1 === undefined && object2 === undefined) {
    return true;
  }

  if (object1 !== undefined && object2 === undefined  || object1 === undefined && object2 !== undefined) {
    return false;
  }

  if (object1 !== null && object2 === null  || object1 === null && object2 !== null) {
    return false;
  }

  if (object1?.['id'] && object2?.['id'] && object1['id'].toString() === object2['id'].toString()) {
    return true;
  }

  const keys1 = Object.keys(object1);
  const keys2 = Object.keys(object2);

  if (keys1.length !== keys2.length) {
    return false;
  }

  for (const key of keys1) {
    const val1 = object1[key];
    const val2 = object2[key];
    const areObjects = isObject(val1) && isObject(val2);
    if (
      areObjects && !deepEqual(val1, val2) ||
      !areObjects && val1 !== val2
    ) {
      return false;
    }
  }

  return true;
}

function isObject(object) {
  return object != null && typeof object === 'object';
}